import React from 'react'
import {useIntl} from 'react-intl'
import {useSelector} from 'react-redux'
import {getIsRtlLanguage} from 'modules/meta/selectors'

import {useDeviceLayout} from '@vio/atlas/context/deviceLayout'
import {Box} from '@vio/atlas/helpers/Box'
import {SmoothScrollNavigation} from '@vio/atlas/helpers/SmoothScrollNavigation'
import {Text} from '@vio/atlas/Text'

import {sectionMessages} from './constants'
import {SimilarHotelCard} from './SimilarHotelCard'
import {SimilarHotelsCarouselLoading} from './SimilarHotelsCarouselLoading'
import {Section, SectionWrapper} from './styles'

import type {SimilarHotelRecommendation} from './types'

interface Props {
  recommendations: SimilarHotelRecommendation[]
  isLoading: boolean
  anchorAvailable: boolean
  anchorName?: string
  onCardClick: (
    recommendation: SimilarHotelRecommendation,
    position: number
  ) => void
}

export const SimilarHotelsCarousel = ({
  recommendations,
  isLoading,
  anchorAvailable,
  anchorName,
  onCardClick
}: Props) => {
  const {formatMessage} = useIntl()
  const {isDesktop} = useDeviceLayout()
  const isRtlLanguage = useSelector(getIsRtlLanguage)
  if (isLoading) return <SimilarHotelsCarouselLoading />
  if (!recommendations.length) return null

  return (
    <SectionWrapper>
      <Section
        aria-busy={false}
        aria-labelledby="anchor-similar-hotels-heading"
        data-testid="anchor-similar-hotels"
      >
        <SmoothScrollNavigation
          responsiveNavigation
          isRtlLanguage={isRtlLanguage}
          navigationButtonSize="md"
          navigationButtonLabels={{
            prev: formatMessage(sectionMessages.previousHotels),
            next: formatMessage(sectionMessages.nextHotels)
          }}
          renderHeader={navigation => (
            <Box
              display="flex"
              alignItems="center"
              gap="s300"
              paddingLeft={isDesktop ? 's700' : 's500'}
              paddingRight={isDesktop ? 's600' : 's500'}
              marginBottom="s400"
            >
              <Box flex="1" minWidth="0">
                <Text
                  id="anchor-similar-hotels-heading"
                  tag="h2"
                  variant={isDesktop ? 'titleL' : 'titleM'}
                >
                  {formatMessage(
                    anchorAvailable
                      ? sectionMessages.nearbyTitle
                      : sectionMessages.availableTitle
                  )}
                </Text>
                {anchorAvailable && anchorName && (
                  <Text
                    variant={isDesktop ? 'bodyS' : 'bodyXS'}
                    colorPath="content.neutral.secondary"
                  >
                    {formatMessage(sectionMessages.comparedWith, {
                      hotelName: anchorName
                    })}
                  </Text>
                )}
              </Box>
              {isDesktop && navigation}
            </Box>
          )}
          hasSidePaddings
          sidePadding={isDesktop ? 's600' : 's400'}
          itemsGap="s400"
        >
          {recommendations.map((recommendation, position) => (
            <SimilarHotelCard
              key={recommendation.hotel.objectID}
              recommendation={recommendation}
              onClick={() => onCardClick(recommendation, position)}
            />
          ))}
        </SmoothScrollNavigation>
      </Section>
    </SectionWrapper>
  )
}
